import React from 'react';
import { FormattedMessage } from 'react-intl';
import { normalizeNameOfPerson } from '../normalize/normalizeNameOfPerson';
import {
    ALL_ELIGIBILITY_TYPES,
    APP_STATUSES,
    APP_TYPES,
    ENROLLMENT_MESSAGES,
    ENROLLMENT_STATUSES,
    ENROLLMENT_TYPES,
    LOCAL_STORAGE,
    RENEWAL_STATUSES,
    SUPER_USERS
} from '@constants/individualPortal/dashboard/dashboardModelTypes';

import Tooltip from '@components/common/tooltip/Tooltip';
import { getDateDifference, isDateAfter, isDateBefore, isDateSame, parseDateWithFormat } from '../dates/date.utils';
import { MM_SLASH_DD_SLASH_YYYY } from '../dates/dateFormats.utils';
import { SEP_END_DATE_SESSION_STORAGE_KEY } from '@constants/common/miscellaneous';



/**
 * Returns true if there is at least one 'DENTAL' type of an enrollment.
 * @param enrollments
 * @param serverDate
 * @returns {*}
 */
const isEnrolledInDental = (enrollments, serverDate) => {
    return enrollments && enrollments.some(i => i.type.toUpperCase() === ENROLLMENT_TYPES.DENTAL
        && !(i.enrollmentStatus === 'TERM' && serverDate.isAfter((i.coverageEndDate))));
};

const isEnrolledInHealth = (enrollments) => {
    return enrollments && enrollments.some(i => i.type.toUpperCase() === ENROLLMENT_TYPES.HEALTH);
};

/**
 * Returns an array of 'HEALTH' enrollments.
 * @param enrollments
 * @returns {*}
 */
export const getHealthEnrollments = enrollments => {
    return enrollments && enrollments.filter(enrollment => {
        return enrollment.type.toUpperCase() === ENROLLMENT_TYPES.HEALTH;
    });
};

/**
 * Returns an array of 'DENTAL' enrollments.
 * @param enrollments
 * @returns {*}
 */
export const getDentalEnrollments = enrollments => {
    return enrollments && enrollments.filter(enrollment => {
        return enrollment.type.toUpperCase() === ENROLLMENT_TYPES.DENTAL;
    });
};

/**
 * Returns true if there is at least one AI/AN applicant
 * @param applicants
 * @returns {*}
 */
const isAIAN = applicants => applicants && applicants.some(i => i.nativeAmerican);



/**
 * Returns true if isShoppingOpenForEntireShoppingWindow is true and current date is with SEP Start Date & End Date
 * @param applicants, sepStartDate, sepEndDate, config
 * @returns {*}
 */

const isMultipleChangePlansAllowed = ({ applicationType, sepStartDate, sepEndDate, isSepOpen, config }) => applicationType === APP_TYPES.QEP && isSepOpen && config.serverDate.isBetween(sepStartDate, sepEndDate, undefined, '[]');

/**
 * Returns true if there is at least one aptc eligible member
 * @param applicants
 * @returns {*}
 */
const isAPTCEligible = applicants => applicants && applicants.some(i => i.aptcEligible);

/**
 * Returns true if period is within OE Enrollment start date and OE enrollment end date.
 */
const isPeriodWithinDates = (config) => {
    let { serverDate, oepStartDate, oepEndDate } = config;
    return serverDate.isBetween(oepStartDate, oepEndDate);
};

/**
 * Returns true if serverDate is before oepStartDate
 * @param applicationYear
 * @param config
 * @returns {boolean}
 */
const isPriorToOEStartDate = (applicationYear, config) => {
    const { serverDate, oepStartDate } = config;
    const appYearGreaterThanServerDate = parseInt(applicationYear) > parseInt(serverDate.year());
    return appYearGreaterThanServerDate && serverDate.isBefore(oepStartDate);
};

/**
 * Renewal Status is considered as 'Set' if the property is not null and Renewals status not equal to "NOT_TO_CONSIDER" OR "APP_ERROR" for either health or dental.
 * @param healthRenewalStatus
 * @param dentalRenewalStatus
 * @returns {boolean}
 */
const isRenewalStatusSet = (healthRenewalStatus, dentalRenewalStatus) => {
    if (!healthRenewalStatus && !dentalRenewalStatus) {
        return false;
    }
    return !([healthRenewalStatus, dentalRenewalStatus].every((renewalStatus) => {
        return renewalStatus === RENEWAL_STATUSES.NOT_TO_CONSIDER || renewalStatus === RENEWAL_STATUSES.APP_ERROR;
    }));
};

const isManualEnrollment = renewalStatus => renewalStatus === RENEWAL_STATUSES.MANUAL_ENROLLMENT;

/**
 * Check if at least one person in the HH is QHP eligible.
 * @param applicants
 * @returns {*}
 */
const isQHPEligible = (applicants) => {
    return applicants && applicants.some((applicant) => applicant.exchangeEligible);
};

/**
 * Check if at least one person in the HH is QHP AND medicaidMAGIEligible.
 * @param applicants
 * @returns {*}
 */
const isQHPMedicaidMAGIEligible = (applicants) => {
    return applicants && applicants.some((applicant) => applicant.exchangeEligible && applicant.medicaidMAGIEligible);
};
/**
 * Returns a map of not eligible for QHP guids.
 * @param applicants
 * @returns {*}
 */
export const getQHPIneligibleMembers = applicants => {
    return applicants && applicants.reduce((acc, applicant) => {
        if (!applicant.exchangeEligible) {
            acc[applicant.guid] = ALL_ELIGIBILITY_TYPES.NOT_QHP;
        }
        return acc;
    }, {});
};

const marketPlaceNotEligible = (applicants) => {
    return applicants && applicants.every(applicant => {
        return !applicant.exchangeEligible;
    });
};

/**
 * Retruns true if at least one enrollment has allowChangePlan flag set to true;
 * @param enrollments
 * @param config
 * @returns {*}
 */
const isChangePlans = (enrollments, config) => {
    const { serverDate } = config;
    return enrollments && enrollments.some(i => i.allowChangePlan === true &&
        !(i.enrollmentStatus === 'TERM' && serverDate.isAfter(i.coverageEndDate)));
};

/**
 * Returns true if all enrollments are not allowed to changed plans;
 * @param enrollments
 * @returns {*}
 */
const isChangePlansNotAllowed = enrollments => {
    return enrollments && enrollments.every(i => i.allowChangePlan !== true);
};

const isSuperUser = (userRole) => {
    return SUPER_USERS.includes(userRole);
};

/**
 * Returns true only if ALL enrollments creation date is within this month.
 * @param enrollments
 * @param serverDate
 */
const isAllEnrollmentCreationDateWithinThisMonth = (enrollments, serverDate) => {
    // We should ignore term enrollments whose coverage end date has passed.
    const filteredEnrollments = enrollments.filter(enrollment => {
        let { enrollmentStatus } = enrollment;
        return enrollmentStatus !== 'TERM';
    });

    return filteredEnrollments.every((enrollment) => {
        let { enrollmentCreationDate } = enrollment;
        return serverDate.isSame(enrollmentCreationDate, 'month');
    });
};

const getEnrolleesNames = enrollees => enrollees && enrollees.map(enrollee => (
    <div key={enrollee.memberId}>
        {enrollee.firstName} {enrollee.middleName} {enrollee.lastName} {enrollee.nameSuffix}
    </div>
));

const getEnrolleesTooltipContent = (enrollees, serverDate) => enrollees && enrollees.map(enrollee => {
    const { memberId, firstName, middleName, lastName, nameSuffix, dob, gender } = enrollee;
    return (
        <div key={memberId}>
            {firstName} {middleName} {lastName} {nameSuffix} { getDateDifference(serverDate, dob, 'years') }y, {gender}
        </div>
    );
});

const getMembers = members => members && members.map(member => (
    <div key={member.externalId}>
        {member.firstName} {member.lastName}
    </div>
));
/*
* Get active tab index
* @param data, getItem
* @returns {number}
* */
const getActiveTabIndex = (data, getItem) => {
    const benefitYearInd = data.findIndex(tabData => tabData && tabData.isBenefitYear);
    const storedTabIndex = getItem(LOCAL_STORAGE.ACTIVE_TAB_INDEX);
    const benefitYearIndex = benefitYearInd !== -1 ? benefitYearInd : data.length - 1;
    return storedTabIndex !== undefined ? storedTabIndex : benefitYearIndex;
};

/**
 *  Store tab data in localStorage
 *  @param data, index
 * */
const storeTabData = (data, index) => {
    const { sepEndDate, ssapApplicationId, caseNumber, encryptedCaseNumber } = data[index];
    localStorage.setItem('tabs', JSON.stringify(data));
    localStorage.setItem('sepEndDate', sepEndDate);
    localStorage.setItem('ssapApplicationId', ssapApplicationId || '');
    localStorage.setItem('enrollCaseNumber', caseNumber);
    localStorage.setItem('encryptedCaseNumber', encryptedCaseNumber);
    sessionStorage.setItem(SEP_END_DATE_SESSION_STORAGE_KEY, sepEndDate);
};

/**
 * Checks if none of the members of a HH are eligible for QHP.
 * @param applicants
 * @returns {*|boolean}
 */
const checkQHPInelibleMembers = applicants => {
    return applicants && applicants.length > 0 && applicants.every(applicant => {
        return !applicant.exchangeEligible || !applicant.seeksQHP;
    });
};

const isAppStatusesEnrolled = (appStatus, appDentalStatus) => {
    return appStatus === APP_STATUSES.EN && appDentalStatus === APP_STATUSES.EN;
};

/**
 * Checks if at least one applicant in the household has sepApplicantEvents with eventName like 'INCOME_CHANGE';
 * @param applicants
 * @param eventsList
 * @returns {boolean}
 */
const checkApplicantEventName = (applicants, eventsList=[]) => {
    if (eventsList && eventsList.length === 0) return false;
    let result = false;

    applicants.forEach(applicant => {
        if (applicant.sepApplicantEvents && applicant.sepApplicantEvents.length > 0) {
            let isSepApplicantEvent = applicant.sepApplicantEvents.some(sepApplicantEvent => {
                return eventsList.includes(sepApplicantEvent.eventName);
            });

            if (isSepApplicantEvent) {
                result = isSepApplicantEvent;
            }
        }
    });

    return result;
};

const checkChangeType = (applicants, changeType) => {
    let result = false;

    applicants.forEach(applicant => {
        if (applicant.sepApplicantEvents && applicant.sepApplicantEvents.length > 0) {
            let isSepApplicantEvent = applicant.sepApplicantEvents.some(sepApplicantEvent => {
                return sepApplicantEvent.changeType === changeType;
            });

            if (isSepApplicantEvent) {
                result = isSepApplicantEvent;
            }
        }
    });

    return result;
};

const getCountChangeType = (applicants, changeType) => {
    let cnt = 0;

    applicants.map(applicant => {
        if (applicant.sepApplicantEvents && applicant.sepApplicantEvents.length > 0) {
            applicant.sepApplicantEvents.map(sepApplicantEvent => {
                if (sepApplicantEvent.changeType === changeType) {
                    cnt++;
                }
            });
        }
    });

    return cnt;
};

/**
 * Filter health enrollments only, calculate if enrollee's age is < 21.
 * @param enrollments
 * @param serverDate
 * @returns {*}
 */
export const processHealthEnrollments = (enrollments, serverDate) => {
    let result = [];
    enrollments.map(enrollment => {
        if (enrollment.type === ENROLLMENT_TYPES.HEALTH) {
            result.push({
                id: enrollment.id,
                plan: {
                    planName: enrollment.plan.planName,
                    netPremium: enrollment.plan.netPremium
                },
                enrollees: enrollment.enrollees.map(enrollee => ({
                    name: `${enrollee.firstName} ${enrollee.middleName && enrollee.middleName} ${enrollee.lastName} ${enrollee.suffix && enrollee.suffix}`,
                    tobaccoIndicator: enrollee.tobaccoIndicator,
                    guid: enrollee.guid,
                    kid: getDateDifference(serverDate, parseDateWithFormat(enrollee.dob, MM_SLASH_DD_SLASH_YYYY), 'years') < 21
                }))
            });
        }
    });
    return result;
};

/**
 * Returns enrollments filtering out Past Term enrollments (whose coverageEndDate is past serverDate)
 * @returns {*|boolean}
 * @param enrollments
 * @param serverDate
 */
export const filterOutPastTermEnrollments = (enrollments, serverDate) => {
    const allowedStatuses = [ENROLLMENT_STATUSES.ENROLLED, ENROLLMENT_STATUSES.PENDING, ENROLLMENT_STATUSES.TERMINATED, ENROLLMENT_STATUSES.CONFIRM];

    return enrollments && enrollments.length > 0 &&
        enrollments.filter(i => allowedStatuses.includes(i.enrollmentStatus) &&
            !(i.enrollmentStatus === ENROLLMENT_STATUSES.TERMINATED && (serverDate.isAfter(i.coverageEndDate) || serverDate.isSame(i.coverageEndDate, 'month')))
        );
};


/**
 * Returns only active enrollments.
 * @param enrollments
 * @returns {*|boolean}
 */
export const getActiveEnrollments = enrollments => {
    const allowedStatuses = [ENROLLMENT_STATUSES.ENROLLED, ENROLLMENT_STATUSES.PENDING, ENROLLMENT_STATUSES.CONFIRM];
    return enrollments && enrollments.length > 0 &&
        enrollments.filter(i => allowedStatuses.includes(i.enrollmentStatus));
};

/**
 * Filters out TERM enrollees whose coverageEndDate is less than tobaccoCoverageDate, also filters out ineligible members.
 * @param enrollments
 * @param tobaccoCoverageDate
 * @param QHPIneligibleMembers
 */
export const filterOutEnrollees = (enrollments, tobaccoCoverageDate, QHPIneligibleMembers) => {
    if (!tobaccoCoverageDate) return enrollments;
    if (!enrollments || enrollments.length === 0) return null;

    return enrollments.reduce((acc, enrollment) => {
        let enrollees = enrollment.enrollees.filter(enrollee => {
            return (enrollee.status !== ENROLLMENT_STATUSES.TERMINATED || isDateAfter(enrollee.coverageEndDate, tobaccoCoverageDate)) &&
                !QHPIneligibleMembers[enrollee.guid];
        });

        if (enrollees.length > 0) {
            acc.push({
                ...enrollment,
                enrollees
            });
        }
        return acc;
    }, []);
};

/**
 * Returns true if previous app id is equal to current app id for some enrollments.
 * @param enrlmnts
 * @param ssapAppId
 */
export const checkAppIds = (enrlmnts, ssapAppId) => enrlmnts && enrlmnts.length > 0 && enrlmnts.every(i => ssapAppId === i.priorSsapApplicationId);

/**
 * Retuns true if at least one member in the HH has seeksQHP=false.
 */
const anyMemberNotSeekingQHP = applicants => applicants && applicants.some((applicant) => applicant.seeksQHP === false);

/**
 * Returns health and/or dental enrollment message.
 * @param hasHealth
 * @param hasDental
 * @returns {{}}
 */
const getEnrollmentReplacements = (hasHealth, hasDental) => {
    const andValue =  { and: hasHealth ? <FormattedMessage id="common.and" /> : '' };

    return {
        health: hasHealth ? <FormattedMessage id="further_action.coverage_status.health"/> : '',
        dental: hasDental ? <FormattedMessage id="further_action.coverage_status.dental" values={ andValue }/> : ''
    };
};

export const renderAmount = (amount) => {
    if(Math.sign(amount) === -1) {
        return '-$'+ Math.abs(amount).toFixed(2);
    }
    return '$' + parseFloat(amount).toFixed(2);
};

export const getPrimaryMember = (applicants) => {
    const primaryMember = applicants && applicants.find(applicant => applicant && applicant.personType && applicant.personType.startsWith('PC'));
    if (!primaryMember) return false;
    const { firstName, middleName, lastName, nameSuffix: suffix } = primaryMember;
    return normalizeNameOfPerson({ firstName, middleName, lastName, suffix });
};

const hasEnrollments = (enrollments) => enrollments && enrollments.length > 0;

const isActiveRenewal = (isInsideOEEnrollmentWindow, oepStartDate, serverDate) =>  isInsideOEEnrollmentWindow && serverDate.isBefore(oepStartDate);

export const getStatus = (type, enrollmentId, status, replacements={}, preDocVerificationShoppingEnabled='false', issuerName='') => {
    let statusProcess = '';
    const { coverageEndDate, effectiveStartDate } = replacements;

    switch (status) {
        case ENROLLMENT_MESSAGES.IN_YOUR_CART: {
            statusProcess = <Tooltip
                id={ENROLLMENT_MESSAGES.IN_YOUR_CART}
                content={ENROLLMENT_MESSAGES.IN_YOUR_CART_TOOLTIP}
                index={`${enrollmentId}'_status_tooltip`}
                place='left'
                contentClassName='tooltip-component'
                ariaLabel={`${issuerName} in your cart`}
            />;
            break;
        }
        case ENROLLMENT_STATUSES.PENDING: {
            statusProcess = <Tooltip
                id={ENROLLMENT_MESSAGES.PENDING}
                content={ENROLLMENT_MESSAGES.PENDING_TOOLTIP}
                index={`${type}_plan_count`}
                place='left'
                contentClassName='tooltip-component'
            />;
            break;
        }
        case ENROLLMENT_STATUSES.PEND: {
            if (['PENDED_PLAN_SELECTION'].includes(preDocVerificationShoppingEnabled)) {
                statusProcess = <Tooltip
                    id={ENROLLMENT_MESSAGES.AWAITING_VERIFICATION}
                    content={ENROLLMENT_MESSAGES.AWAITING_VERIFICATION_TOOLTIP}
                    index={`${type}_plan_count`}
                    place='left'
                    contentClassName='tooltip-component'
                />;
            }
            break;
        }
        case ENROLLMENT_STATUSES.TERMINATED: {
            statusProcess = <FormattedMessage id={ENROLLMENT_MESSAGES.TERMINATED}/>;
            break;
        }
        case ENROLLMENT_STATUSES.FUTURE_TERM: {
            statusProcess = <FormattedMessage id={ENROLLMENT_MESSAGES.FUTURE_TERM} values={{ coverageEndDate }}/>;
            break;
        }
        case ENROLLMENT_STATUSES.ENROLLED: {
            statusProcess = <FormattedMessage id={ENROLLMENT_MESSAGES.ENROLLED}/>;
            break;
        }
        case ENROLLMENT_STATUSES.CONFIRM: {
            statusProcess = <FormattedMessage id={ENROLLMENT_MESSAGES.CONFIRM}/>;
            break;
        }
        case ENROLLMENT_STATUSES.SAVED_PLAN: {
            statusProcess = <Tooltip
                id={ENROLLMENT_MESSAGES.SAVED_PLAN}
                content={ENROLLMENT_MESSAGES.SAVED_PLAN_TOOLTIP}
                index={`${type}_plan_count`}
                contentReplacement={{ effectiveStartDate }}
                place='top'
                contentClassName='tooltip-component'
            />;
            break;
        }
        default:
            statusProcess =
                status[0] + status.slice(1).toLowerCase();
            break;
    }
    return statusProcess;
};

/**
 * Returns true if Health enrollment has TERM status and it's coverageEndDate month is same or past serverDate month
 * OR an enrollee has TERM status and it's coverageEndDate month is same or past serverDate month.
 * @param enrollments
 * @param serverDate
 * @returns {boolean|*}
 */
const isTermEnrollee = (enrollments, serverDate) => {
    const healthEnrollments = getHealthEnrollments(enrollments);
    if (!healthEnrollments || healthEnrollments.length < 0) return false;

    const checkTermCoverageEndDate = (status, coverageEndDate, serverDate) => {
        return (status === ENROLLMENT_STATUSES.TERMINATED && (isDateBefore(coverageEndDate, serverDate) ||
            isDateSame(serverDate, coverageEndDate, 'month')));
    };
    const checkTermEnrollee = ({ enrollees }) => enrollees.some(({ status, coverageEndDate }) => {
        return checkTermCoverageEndDate(status, coverageEndDate, serverDate);
    });
    return healthEnrollments.some(({ enrollmentStatus, coverageEndDate }) => checkTermCoverageEndDate(enrollmentStatus, coverageEndDate, serverDate))
        || healthEnrollments.some(checkTermEnrollee);
};

/**
 * Returns true if lowFplToEnableOnceAMonthShopping = true and "fpl" from getDashboardModels <= lowFplOnceAMonthShoppingThreshold
 * from environment API AND at least one member APTC eligible.
 * @param fpl
 * @param applicants
 * @param lowFplToEnableOnceAMonthShopping
 * @param lowFplOnceAMonthShoppingThreshold
 * @returns {*|boolean}
 */
const checkFPL = (fpl, applicants, lowFplToEnableOnceAMonthShopping, lowFplOnceAMonthShoppingThreshold) => {
    if (fpl == null || !lowFplToEnableOnceAMonthShopping) return false;
    return  isAPTCEligible(applicants) && fpl <= lowFplOnceAMonthShoppingThreshold ;
};

export const processCommon = {
    anyMemberNotSeekingQHP,
    checkApplicantEventName,
    checkChangeType,
    checkFPL,
    checkQHPInelibleMembers,
    getActiveTabIndex,
    getCountChangeType,
    getDentalEnrollments,
    getEnrolleesNames,
    getEnrolleesTooltipContent,
    getEnrollmentReplacements,
    getMembers,
    hasEnrollments,
    isAIAN,
    isAPTCEligible,
    isActiveRenewal,
    isAllEnrollmentCreationDateWithinThisMonth,
    isAppStatusesEnrolled,
    isChangePlans,
    isChangePlansNotAllowed,
    isEnrolledInDental,
    isEnrolledInHealth,
    isManualEnrollment,
    isMultipleChangePlansAllowed,
    isPeriodWithinDates,
    isPriorToOEStartDate,
    isQHPEligible,
    isQHPMedicaidMAGIEligible,
    isRenewalStatusSet,
    isSuperUser,
    isTermEnrollee,
    marketPlaceNotEligible,
    storeTabData
};
